import AddIcon from '@mui/icons-material/Add'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'
import { type CSSProperties, type MouseEvent, useEffect, useMemo, useRef, useState } from 'react'
import {
  type SaleClaim,
  useDeleteSaleClaimMutation,
  useGetSaleClaimCanHardDeleteQuery,
  useGetSaleClaimsQuery,
} from '../../store/api/saleClaimApi'
import ClaimFormDialog from './ClaimFormDialog'
import ClaimPrintDialog from './ClaimPrintDialog'
import { HIDE_ON_MOBILE_INLINE, HIDE_ON_TABLET_INLINE, TABLE_PAGINATION_COMPACT } from '../../theme/responsive'
import BrandState from '../../components/BrandState'
import ConfirmDialog from '../../components/ConfirmDialog'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
} from '../../components/dataList'
import ResizeHandle from '../../components/ResizeHandle'
import { useColumnWidths } from '../../hooks/useColumnWidths'
import { useIsMobile } from '../../hooks/useResponsive'
import { usePagePermission } from '../../hooks/usePagePermission'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '../../utils/tableSort'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'
import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'

type FilterKind = 'search'
type FinalizedFilter = 'all' | 'yes' | 'no'

/** Freeze the Claim Code identifier column when scrolling horizontally. */
const FROZEN_KEYS: ReadonlySet<string> = new Set(['claimScsCode'])
const FROZEN_FALLBACK_WIDTH_DESKTOP: Record<string, number> = { claimScsCode: 120 }
const FROZEN_FALLBACK_WIDTH_MOBILE: Record<string, number> = { claimScsCode: 110 }

const ALL_ROWS_PAGE_SIZE = 1_000_000

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

type ColumnKind = 'text' | 'date' | 'datetime' | 'number' | 'int'
type ColumnDef = {
  key: keyof SaleClaim
  label: string
  kind?: ColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
  wrap?: boolean
  width?: number
}

/**
 * Column order + list follow Access `frmCtrlSalesQryClaim` datasheet `ColumnOrder`
 * (source form_web_ui_structure_sorted.csv, 52 cols). This form has a PARTIAL ColumnOrder:
 * the claim block is explicitly ordered (ColumnOrder 1–38) and comes first (`claim_scs_id`
 * = ID at #1, not hidden → shown); the debit-note block has ColumnOrder=0 (UNSET) so it is
 * appended LAST in TabIndex order (convert-structure treats ColumnOrder=0 as unset).
 * No ComboBox/ListBox columns → no RowSource lookup mapping (convert-table §4.1).
 * `claim_value_final` is NOT in the Access datasheet ColumnOrder → not displayed.
 * ID = integer no separator; claimId2/claimId3 are string refs (text).
 */
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'id', label: 'ID', kind: 'int', align: 'right' },
  { key: 'claimScsCode', label: 'Claim No.' },
  { key: 'dateClaim', label: 'Date claim', kind: 'date' },
  { key: 'customerInvoiceNo', label: 'Customer invoice number' },
  { key: 'forDo', label: 'DO No.' },
  { key: 'contractRef', label: 'Contract ref.' },
  { key: 'client', label: 'Client' },
  { key: 'claimType', label: 'Type of claim', filter: 'checkboxes' },
  { key: 'claimCondition', label: 'Conditions', filter: 'checkboxes' },
  { key: 'customerInvAtd', label: 'ATD', kind: 'date' },
  { key: 'dischargeDate', label: 'Discharge date', kind: 'date' },
  { key: 'weighingDate', label: 'Weighing date', kind: 'date' },
  { key: 'validWeighingDate', label: 'Valid weighing date', kind: 'date' },
  { key: 'validClaimDate', label: 'Valid claim date', kind: 'date' },
  { key: 'acceptOrReject', label: 'Accept/ Reject?', filter: 'checkboxes' },
  { key: 'finalStatus', label: 'Final status', filter: 'checkboxes' },
  { key: 'paymentWeightTerms', label: 'Payment weight terms' },
  { key: 'price', label: 'Price', kind: 'number', align: 'right' },
  { key: 'grossShippedWeight', label: 'Gross shipped weight', kind: 'number', align: 'right' },
  { key: 'netShippedWeight', label: 'Net shipped weight', kind: 'number', align: 'right' },
  { key: 'receiptWh', label: 'WH Receipt' },
  { key: 'grossLandedWeight', label: 'Gross landed weight', kind: 'number', align: 'right' },
  { key: 'netLandedWeight', label: 'Net landed weight', kind: 'number', align: 'right' },
  { key: 'difference', label: 'Difference', kind: 'number', align: 'right' },
  { key: 'franchise', label: 'Franchise', kind: 'number', align: 'right' },
  { key: 'claimWeight', label: 'Weight claim', kind: 'number', align: 'right' },
  { key: 'claimWeightValue', label: 'Weight Value claim', kind: 'number', align: 'right' },
  { key: 'claimReason', label: 'Reason claim', wrap: true, width: 220 },
  { key: 'claimQualityValue', label: 'Quality Value claim', kind: 'number', align: 'right' },
  { key: 'claimOtherValue', label: 'Other Value claim', kind: 'number', align: 'right' },
  { key: 'creditNo', label: 'Credit note No.' },
  { key: 'fullCreditNo', label: 'Full credit No.' },
  { key: 'claimId2', label: 'Claim ID2' },
  { key: 'creditDate', label: 'Date', kind: 'date' },
  { key: 'creditAmount', label: 'Credited Amount', kind: 'number', align: 'right' },
  { key: 'priceUnit', label: 'Price Unit' },
  { key: 'customerDebitNo', label: "Customer's Debit No." },
  { key: 'fromWarehouse', label: 'Loading Place' },
  { key: 'debitDate', label: 'DN date', kind: 'date' },
  { key: 'debitNo', label: 'Debit Note No.' },
  { key: 'fullDebitNo', label: 'Full Debit No.' },
  { key: 'claimId3', label: 'Claim ID3' },
  { key: 'debitorTo', label: 'Debitor to' },
  { key: 'debitCpCode', label: 'Debit Code' },
  { key: 'debitName', label: 'Debitor Name' },
  { key: 'debitorRefNo', label: "Debitor's Ref No." },
  { key: 'debitReason', label: 'Reason for Debit', wrap: true, width: 220 },
  { key: 'debitValue', label: 'Value debit', kind: 'number', align: 'right' },
  { key: 'roe', label: 'ROE', kind: 'number', align: 'right' },
  { key: 'claimNote', label: 'Remark', wrap: true, width: 220 },
  { key: 'dateUpdated', label: 'Date Updated' },
  { key: 'personUpdated', label: 'Person Updated' },
]

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  return `${day}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()}`
}

const formatAccessNumber = (value: number): string => {
  if (Number.isInteger(value)) return value.toLocaleString('en-US')
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const sumNumericColumn = (rows: SaleClaim[], key: keyof SaleClaim): number | null => {
  let total = 0
  let count = 0
  for (const row of rows) {
    const value = row[key]
    if (typeof value !== 'number' || !Number.isFinite(value)) continue
    total += value
    count += 1
  }
  return count > 0 ? total : null
}

interface SaleControlClaimViewProps {
  shipmentPeriodStart?: string
  shipmentPeriodEnd?: string
  finalizedFilter?: FinalizedFilter
}

export default function SaleControlClaimView({
  shipmentPeriodStart = '',
  shipmentPeriodEnd = '',
  finalizedFilter = 'all',
}: SaleControlClaimViewProps) {
  const { canEdit } = usePagePermission()
  const isMobile = useIsMobile()
  const FROZEN_FALLBACK_WIDTH = isMobile ? FROZEN_FALLBACK_WIDTH_MOBILE : FROZEN_FALLBACK_WIDTH_DESKTOP
  const snack = useSnackbar()
  const { selectedId, setSelectedId, toggleSelect } = useRowSelection<number>()
  const [confirmDelete, setConfirmDelete] = useState<SaleClaim | null>(null)
  const [formState, setFormState] = useState<{ mode: 'new' | 'edit'; claimId: number | null } | null>(null)
  const [printState, setPrintState] = useState<{ open: boolean; claimId: number | null; claimScsCode: string | null }>({
    open: false,
    claimId: null,
    claimScsCode: null,
  })

  const { data: deleteAccess } = useGetSaleClaimCanHardDeleteQuery(undefined, {
    refetchOnMountOrArgChange: true,
  })
  const canHardDelete = canEdit && (deleteAccess?.canDelete ?? false)
  const [deleteSaleClaim, { isLoading: deleting }] = useDeleteSaleClaimMutation()

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const { getWidth, startResize } = useColumnWidths('sale-claim.list')

  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<keyof SaleClaim> | null>(null)

  const filterPopover = useFilterPopover<FilterKind>()
  const closeFilterPopover = () => filterPopover.close()
  const openFilterPopover = (kind: FilterKind, e: MouseEvent<HTMLElement>) => filterPopover.open(kind, e)

  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: keyof SaleClaim
    position: ColumnFilterAnchorPosition
  } | null>(null)
  const closeColPopover = () => setColPopover(null)

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    closeFilterPopover()
  }

  const columnsCtrl = usePreferenceColumns({
    scope: 'sale_control',
    prefKey: 'columns',
    subKey: 'claim',
    allColumns: TABLE_COLUMNS,
    widthsTableKey: 'sale-claim.list',
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl

  const {
    data: list,
    isLoading,
    isFetching,
    error,
  } = useGetSaleClaimsQuery(
    {
      page: 1,
      pageSize: ALL_ROWS_PAGE_SIZE,
      ...(shipmentPeriodStart ? { shipmentPeriodFrom: shipmentPeriodStart } : {}),
      ...(shipmentPeriodEnd ? { shipmentPeriodTo: shipmentPeriodEnd } : {}),
      finalizedFilter,
    },
    { refetchOnMountOrArgChange: true },
  )

  const baseRows = useMemo(() => list?.items ?? [], [list])
  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (row) =>
          rowMatchesSearch(row, searchTerm, TABLE_COLUMNS) &&
          rowMatchesAllColumnFilters(row, columnFilters.filters),
      ),
    [baseRows, columnFilters.filters, searchTerm],
  )
  const sortedRows = useMemo(() => sortTableRows(filteredRows, sortState, TABLE_COLUMNS), [filteredRows, sortState])
  const rows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])
  const totalRowValues = useMemo(
    () =>
      new Map(
        visibleColumns
          .filter((col) => col.kind === 'number')
          .map((col) => [col.key, sumNumericColumn(filteredRows, col.key)] as const),
      ),
    [filteredRows, visibleColumns],
  )
  const popoverColumn = useMemo<ColumnDef | null>(() => {
    if (!colPopover) return null
    return TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null
  }, [colPopover])
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () => (popoverKind === 'checkboxes' && colPopover ? distinctColumnValues(baseRows, String(colPopover.key)) : undefined),
    [popoverKind, colPopover, baseRows],
  )

  const minTableWidth = Math.max(960, visibleColumns.length * 96)
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`

  const toggleSort = (key: keyof SaleClaim) => {
    setSortState((current) => toggleTableSort(current, key))
    setPage(0)
  }

  const selectedRow = useMemo<SaleClaim | null>(
    () => rows.find((r) => r.id === selectedId) ?? null,
    [rows, selectedId],
  )

  // Access frmCtrlSalesQryClaim Form_Open has no GoToRecord → Access focuses the first
  // datasheet record. List is claim_scs_id DESC, so auto-select the top row once.
  const didAutoSelect = useRef(false)
  useEffect(() => {
    if (didAutoSelect.current || isFetching) return
    if (sortedRows.length > 0 && selectedId == null) {
      setSelectedId(sortedRows[0].id)
      didAutoSelect.current = true
    }
  }, [isFetching, sortedRows, selectedId, setSelectedId])

  // ── Toolbar actions (Access frmCtrlSalesMainSubFormClaim, Left order) ──────
  const requireRow = (): SaleClaim | null => {
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return null
    }
    return selectedRow
  }

  // btnNew / btnEdit → frmCtrlSalesQryClaimAdd (ClaimFormDialog). btnPrint →
  // frmCtrlSalesQryClaim_Choose → rpCtrlSalesClaimCheck* (ClaimPrintDialog).
  const handleNew = () => {
    if (!canEdit) return
    setFormState({ mode: 'new', claimId: null })
  }
  const handleEdit = () => {
    if (!canEdit) return
    const row = requireRow()
    if (!row) return
    setFormState({ mode: 'edit', claimId: row.id })
  }
  const handlePrint = () => {
    const row = requireRow()
    if (!row) return
    if (!row.customerInvoiceNo) {
      snack.warning('Customer invoice no must not be null!')
      return
    }
    setPrintState({ open: true, claimId: row.id, claimScsCode: row.claimScsCode })
  }

  const handleDeleteClick = () => {
    if (!requireRow()) return
    setConfirmDelete(selectedRow)
  }

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return
    try {
      await deleteSaleClaim(confirmDelete.id).unwrap()
      snack.success('Successfully Deleted')
      if (selectedId === confirmDelete.id) setSelectedId(null)
      setConfirmDelete(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Delete claim failed'))
    }
  }

  const cellValue = (row: SaleClaim, col: ColumnDef): string => {
    const val = row[col.key]
    if (val == null) return ''
    if (col.kind === 'date') return formatAccessDate(val)
    if (col.kind === 'int' && typeof val === 'number') return String(Math.round(val))
    if (col.kind === 'number' && typeof val === 'number') return formatAccessNumber(val)
    if (typeof val === 'number') return formatAccessNumber(val)
    return String(val)
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' },
      }}
    >
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
            {/* Button order follows Access `frmCtrlSalesMainSubFormClaim` (Left order):
                New · Edit · Delete · Print. All gated by SCREEN_ACCESS. */}
            <Button
              variant="contained"
              size="small"
              color="success"
              sx={actionToolbarBtnSx}
              disabled={!canEdit}
              onClick={handleNew}
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              title="New"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>New</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="warning"
              sx={actionToolbarBtnSx}
              disabled={!selectedRow || !canEdit}
              onClick={handleEdit}
              startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Edit"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Edit</Box>
            </Button>
            {canHardDelete && (
              <Button
                variant="outlined"
                size="small"
                color="error"
                sx={actionToolbarBtnSx}
                disabled={!selectedRow || !canEdit}
                onClick={handleDeleteClick}
                startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                title="Delete"
              >
                <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Delete</Box>
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }}
              disabled={!selectedRow || !canEdit}
              onClick={handlePrint}
              startIcon={<PrintOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Print"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Print</Box>
            </Button>
          </Stack>
        }
      >
        <FilterButton label="Search" active={!!searchTerm.trim()} onClick={(e) => openFilterPopover('search', e)} />
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => openColMenu(e.currentTarget)}
          sx={filterBarBtnSx}
          title="Columns"
        >
          <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
        </Button>
      </FilterBar>

      <Divider sx={{ mb: 0.5 }} />

      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          Failed to load Claims.
        </Alert>
      ) : null}

      <Box className="app-dataframe">
        <Box
          className="app-dataframe-toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.5,
            flexWrap: 'wrap',
            minHeight: 24,
            py: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Total: {filteredRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Loaded: {baseRows.length}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Stack>
        </Box>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={closeFilterPopover}
          maxWidth={320}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0) }}
              placeholder="Claim code, invoice, client..."
              label="Search claims"
              onClear={closeFilterPopover}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={closeColPopover}
          label={popoverColumn?.label ?? ''}
          kind={(popoverKind === 'none' ? 'text' : popoverKind)}
          value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(String(colPopover.key), v)
            setPage(0)
          }}
          options={popoverOptions}
        />

        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          {error ? (
            <BrandState
              tone="error"
              title="Could not load Claims"
              description="Please refresh the page or contact the system administrator if the issue continues."
            />
          ) : isLoading || isFetching ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState
              title="No Claim found"
              description="Try adjusting the saved shipment period, finalized filter, or the table filters above."
            />
          ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={rows}
              fixedHeaderContent={() => (
                <TableRow>
                  {visibleColumns.map((col) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                    const headerStyle: CSSProperties = {}
                    if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
                    if (isFrozen) {
                      headerStyle.position = 'sticky'
                      headerStyle.top = 0
                      headerStyle.left = 0
                      headerStyle.zIndex = 100
                    }
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(col.key)}
                        style={Object.keys(headerStyle).length ? headerStyle : undefined}
                        sx={{
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          userSelect: 'text',
                          ...(isFrozen ? {} : { position: 'relative' }),
                        }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState, col.key)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(key)}
                          onClick={(position) => setColPopover({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(key, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              fixedFooterContent={() => (
                <TableRow>
                  {visibleColumns.map((col, index) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const total = totalRowValues.get(col.key)
                    const val = total == null ? '' : formatAccessNumber(total)
                    const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          py: 0.5,
                          bgcolor: 'background.paper',
                          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {index === 0 ? 'Total' : val}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: SaleClaim) =>
                visibleColumns.map((col) => {
                  const key = String(col.key)
                  const isFrozen = FROZEN_KEYS.has(key)
                  const val = cellValue(row, col)
                  const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                  const wrap = col.wrap || getWidth(key) != null
                  if (wrap) {
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }}
                      >
                        <Box component="span" className="app-table-cell-wrap">
                          {val || '-'}
                        </Box>
                      </TableCell>
                    )
                  }
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align}
                      className={isFrozen ? 'app-sticky-col' : undefined}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top' }}
                    >
                      <Box component="span" className="app-table-cell-text" title={val || '-'} sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {val || '-'}
                      </Box>
                    </TableCell>
                  )
                })
              }
              components={{
                Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />,
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                TableRow: (props: any) => {
                  const idx = Number((props as any)['data-item-index'])
                  const rowId = Number.isFinite(idx) ? rows[idx]?.id : null
                  return (
                    <TableRow
                      {...props}
                      hover
                      selected={rowId != null && rowId === selectedId}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (rowId == null) return
                        toggleSelect(rowId)
                      }}
                    />
                  )
                },
              }}
            />
          )}
        </Box>
      </Box>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete Claim"
        message={confirmDelete ? `Do you want to delete Claim = '${confirmDelete.claimScsCode ?? ''}'?` : ''}
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDelete(null)}
      />
      <FuturesColumnsMenu controller={columnsCtrl} />
      {formState && (
        <ClaimFormDialog
          open
          mode={formState.mode}
          claimId={formState.claimId}
          canEdit={canEdit}
          onClose={() => setFormState(null)}
          onSaved={() => setSelectedId(null)}
        />
      )}
      <ClaimPrintDialog
        open={printState.open}
        claimId={printState.claimId}
        claimScsCode={printState.claimScsCode}
        canEdit={canEdit}
        onClose={() => setPrintState({ open: false, claimId: null, claimScsCode: null })}
      />
    </Box>
  )
}
